/**
 * ModalHeader
 *
 * Renders a header bar for a modal.  Only use as a child of Modal.
 * See modal.jsx for more details.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {baseBorderRadius, mediaQueries} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

// TODO(kevinb) replace with React.Node after updating to React v16

class ModalHeader extends React.Component<{
    // Identifier to use for the main content so that it can be indicated
    // as the accessible modal title. This is provided automatically by the
    // Modal component.
    id?: string;
    ariaLabel?: string;
    children?: React.ReactNode;
    // TODO(riley): Remove this prop in favor of `style`.
    backgroundColor?: string;
    borderColor?: string;
    // Configures the modal heading into a link.
    titleHref?: string;
    openTitleLinkInNewTab?: boolean;
    onTitleNavigation?: () => void;
    style?: {
        backgroundColor?: string;
        backgroundImage?: string;
        backgroundPosition?: string;
        backgroundRepeat?: string;
        backgroundSize?: string;
        color?: string;
    };
    // Merged in by Modal -- these shouldn't be passed in by
    // clients directly.
    topLeftCorner?: React.ReactNode | null | undefined;
    topRightCorner?: React.ReactNode | null | undefined;
}> {
    static defaultProps: {
        borderColor: string;
    } = {
        borderColor: "rgba(0, 0, 0, 0.15)",
    };

    renderTitle: (corners: "small" | "large" | null) => React.ReactNode = (
        corners: "small" | "large" | null,
    ) => {
        const {
            ariaLabel,
            children,
            openTitleLinkInNewTab,
            titleHref,
            onTitleNavigation,
        } = this.props;

        // HACK: use a hard cutoff to determine if a title is long. This
        // is mostly to avoid egregious wrapping and was empirically
        // determined using title lengths at the title font size on an
        // iPhone 5.
        const longTitle = typeof children === "string" && children.length > 32;

        if (!titleHref) {
            return (
                <HeadingMedium
                    aria-label={ariaLabel}
                    id={this.props.id}
                    style={[
                        styles.titleWrapper,
                        styles.title,
                        longTitle && styles.titleLong,
                    ]}
                    tag="h1"
                    testId="modal-title"
                >
                    <span aria-hidden={true}>{children}</span>
                </HeadingMedium>
            );
        }

        return (
            <Link
                href={titleHref}
                style={[
                    styles.titleWrapper,
                    corners === "large" && styles.titleWithLargeCorners,
                    corners === "small" && styles.titleWithSmallCorners,
                ]}
                target={openTitleLinkInNewTab ? "_blank" : undefined}
                onClick={onTitleNavigation}
            >
                <HeadingMedium
                    id={this.props.id}
                    style={[styles.title, longTitle && styles.titleLong]}
                    tag="h1"
                >
                    <span className="sr-only">{ariaLabel}</span>
                    <span aria-hidden={true} data-testid="modal-title">
                        {children}
                    </span>
                </HeadingMedium>
            </Link>
        );
    };

    render(): React.ReactNode {
        const {
            backgroundColor,
            borderColor,
            style,
            topLeftCorner,
            topRightCorner,
        } = this.props;

        const extraStyles: any = {
            // Default styles.
            color: color.offBlack,

            // Passed styles.
            ...style,

            // Additional styles.
            boxShadow: `0 1px 0 0 ${borderColor || ""}`,
        };

        if (backgroundColor) {
            extraStyles.backgroundColor = backgroundColor;
        }

        // When we have a top right corner (which can be arbitrarily large --
        // see the topRightElements prop on Modal), then render both
        // corners at a large but fixed size. If we only have a top left corner,
        // then we still render both corners, but at a smaller size that's just
        // large enough for the 'Close' button that will be rendered.
        const corners = topRightCorner
            ? "large"
            : topLeftCorner
              ? "small"
              : null;

        return (
            <div style={extraStyles} className={css(styles.header)}>
                {corners && (
                    <div
                        className={css(
                            styles.topLeftCornerInHeader,
                            corners === "large" && styles.largeCornerInHeader,
                            corners === "small" && styles.smallCornerInHeader,
                        )}
                    >
                        {topLeftCorner}
                    </div>
                )}

                {this.renderTitle(corners)}

                {corners && (
                    <div
                        className={css(
                            styles.topRightCornerInHeader,
                            corners === "large" && styles.largeCornerInHeader,
                            corners === "small" && styles.smallCornerInHeader,
                        )}
                    >
                        {topRightCorner}
                    </div>
                )}
            </div>
        );
    }
}

// TODO(kevinb): dedupe with modal-footer.jsx
const headerHeight = 60;
const smallCornerWidth = 56;
const largeCornerWidth = 152;

const styles = StyleSheet.create({
    // Header styles
    header: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 0,
        flexShrink: 0,
        position: "relative",
        // The header and footer have semi-transparent "borders", implemented
        // with box-shadow. Some of our modal content is positioned with its
        // own stacking context. We give ModalHeader a z-index so that such
        // content doesn't overlap & hide the "border".
        //
        // The footer doesn't need a z-index because it comes after the
        // content; it overlaps by default. This is convenient since we want
        // dropdowns etc. to overlap the footer.
        zIndex: 1,
        height: headerHeight,
        borderTopLeftRadius: baseBorderRadius,
        borderTopRightRadius: baseBorderRadius,
    },
    largeCornerInHeader: {
        // The width of the most common element rendered in the corner -- the
        // "Go to lesson page" English text.
        // TODO(charlie): Implement a dynamic layout here. I'm not sure that
        // it's possible in Flexbox to keep the left and right corners equally
        // sized, at the 'max width' of either element.
        flexBasis: largeCornerWidth,
        [mediaQueries.smOrSmaller]: {
            flexBasis: smallCornerWidth,
        },
    },
    smallCornerInHeader: {
        flexBasis: smallCornerWidth,
        [mediaQueries.smOrSmaller]: {
            flexBasis: smallCornerWidth,
        },
    },
    topLeftCornerInHeader: {
        justifyContent: "flex-start",
    },
    topRightCornerInHeader: {
        justifyContent: "flex-end",
    },
    titleWrapper: {
        display: "flex",
        flex: "0 0 auto",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        paddingLeft: 6,
        paddingRight: 6,
        margin: "0 auto",
        maxWidth: "100%",
    },
    titleWithLargeCorners: {
        // Adding a max width so that we prevent unwanted overflow of the
        // title. We allow it to take up the remaining space of the header
        // after allocating some space for the right and left corners.
        maxWidth: `calc(100% - ${largeCornerWidth * 2}px)`,

        [mediaQueries.smOrSmaller]: {
            maxWidth: `calc(100% - ${smallCornerWidth * 2}px)`,
        },
    },
    titleWithSmallCorners: {
        // Adding a max width so that we prevent unwanted overflow of the
        // title. We allow it to take up the remaining space of the header
        // after allocating some space for the right and left corners.
        maxWidth: `calc(100% - ${smallCornerWidth * 2}px)`,

        [mediaQueries.smOrSmaller]: {
            maxWidth: `calc(100% - ${smallCornerWidth * 2}px)`,
        },
    },
    title: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        [mediaQueries.smOrSmaller]: {
            ...deprecatedTypography.subheadingMobile,
            color: "inherit",
        },
    },
    titleLong: {
        [mediaQueries.smOrSmaller]: {
            ...deprecatedTypography.smallHeadingMobile,
            color: "inherit",
        },
    },
});

export default ModalHeader;
